import {
  ASSET_PREFILL_STORAGE_KEY,
  BUSINESS_INFO_STORAGE_KEY,
  PERSONAL_INFO_STORAGE_KEY,
  PREFILL_SUGGESTIONS_STORAGE_KEY,
} from '../constants/headsUp';
import { US_STATES } from '../types/constants';
import {
  AddressParts,
  getFullAddress,
  isCompleteAddress,
} from './AddressUtils';
import { formatUSPhoneForDisplay, parseUSPhoneToE164 } from './phone';
// The session wrappers this file used to define privately now come from the
// shared module — same semantics, one implementation. Aliased so the call
// sites below keep reading in this file's own vocabulary.
import {
  safeSessionGetItem as readSession,
  safeSessionRemoveItem as removeSession,
  safeSessionSetItem as writeSession,
} from '../shared/safeStorage';

/**
 * Highway prefill consumption (THT-711 workstream b).
 *
 * Everything here is pure apart from the session helpers near the bottom, which is the point:
 * the merge rules are the whole risk surface of this feature (account data must win, a value
 * the customer typed must never be overwritten, a missing payload must change nothing), and
 * they are far easier to trust as functions of their inputs than as effects inside a
 * 1000-line form.
 *
 * The write target is the EXISTING `businessInfo` / `personalInfo` sessionStorage shapes,
 * merged into rather than replaced, so `populateApplicationData`'s returning-applicant
 * hydration and BusinessInfo's own `businessInfo` guard keep working unchanged.
 *
 * Alongside the values we record the SUGGESTION each prefilled field was given. That record is
 * what lets a customer who goes back and confirms a DIFFERENT carrier have the first carrier's
 * untouched suggestions replaced rather than protected as data worth keeping — the merge rules
 * cannot otherwise tell a value we filled in from one the customer typed.
 */

/** The address block as the endpoint returns it — already split server-side. */
export type CarrierPrefillAddress = {
  street_number?: string;
  street_name?: string;
  unit?: string;
  city?: string;
  state?: string;
  zip?: string;
};

/**
 * The `prefill` object from `GET /highway/carrier-prefill/{number}`. Every field is optional
 * except the legal name. There are deliberately no tax identifiers, no revenue, no personal
 * address and no truck-age fields in this type — see the never-prefill list in
 * `docs/audit-prefill-highway-carrier-flow.md`; a field absent from the type cannot be written
 * by accident.
 */
export type CarrierPrefill = {
  legal_name: string;
  dba_name?: string;
  address?: CarrierPrefillAddress;
  phone?: string;
  email?: string;
  years_in_business?: number;
  owned_tractors?: number;
  owned_trailers?: number;
  contact_first_name?: string;
  contact_last_name?: string;
};

/**
 * What we already know about this customer from OUR OWN records — the customer row and its
 * primary driver. This is the data that beats Highway everywhere the two overlap: the customer
 * gave it to us directly, Highway's copy was scraped from a registry.
 */
export type PrefillAccountSnapshot = {
  businessName?: string;
  businessPhone?: string;
  address?: Partial<AddressParts>;
  /** Beats the address-derived guess below — this one the customer actually told us. */
  stateOfRegistration?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  /**
   * Not a prefill source — Highway has no factoring data and never will. It is carried here
   * only so the write below can preserve BusinessInfo's own factoring restore; see
   * `buildBusinessInfoPrefill`.
   */
  factoringCompanyName?: string;
};

/** What a step's prefilled fields were suggested, keyed by the field name it stores under. */
export type SectionSuggestions = Record<string, string>;

/**
 * The suggestion record, one entry per step, plus which confirmed carrier produced it. The
 * carrier key is what tells a later apply whether it is topping up its own work or replacing
 * a different carrier's.
 */
export type PrefillSuggestions = {
  carrierKey: string;
  businessInfo: SectionSuggestions;
  personalInfo: SectionSuggestions;
  assets: SectionSuggestions;
};

/** Counts the Assets step reads back. Absent entirely when Highway had no equipment data. */
export type AssetPrefill = {
  num_trucks?: number;
  num_trailers?: number;
};

/** Section keys the suggestion reader accepts. */
export const PREFILL_SECTION_BUSINESS_INFO = 'businessInfo';
export const PREFILL_SECTION_PERSONAL_INFO = 'personalInfo';
export const PREFILL_SECTION_ASSETS = 'assets';

/**
 * Field names the writer here shares with the session shapes the steps hydrate from. They were
 * bare literals until the review pointed out that a rename would break the suggestion record
 * silently — nothing type-checks a string against a string.
 */
export const PREFILL_FIELD_BUSINESS_NAME = 'businessName';
export const PREFILL_FIELD_BUSINESS_PHONE = 'businessPhone';
export const PREFILL_FIELD_YEARS_IN_BUSINESS = 'yearsInBusiness';
export const PREFILL_FIELD_STATE_OF_REGISTRATION = 'state_of_registration';
export const PREFILL_FIELD_FIRST_NAME = 'firstName';
export const PREFILL_FIELD_LAST_NAME = 'lastName';
export const PREFILL_FIELD_EMAIL = 'email';
export const PREFILL_FIELD_PHONE_NUMBER = 'phoneNumber';
export const PREFILL_FIELD_NUM_TRUCKS = 'num_trucks';
export const PREFILL_FIELD_NUM_TRAILERS = 'num_trailers';

/** The composed one-line address FlexibleAddressInput opens on. */
export const PREFILL_FIELD_BUSINESS_ADDRESS = 'businessAddress';

/** The six parsed columns behind it, in the order `getFullAddress` reads them. */
export const PREFILL_ADDRESS_PART_FIELDS = [
  'address_st_number',
  'address_st_name',
  'address_unit',
  'address_city',
  'address_state',
  'address_zip',
] as const;

export const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * The state as the registration select can show it, or nothing.
 *
 * A value outside its options is worse than an empty select: react-hook-form would hold
 * something the customer can neither see nor clear, and the form would submit it. Highway
 * returns US postal codes, but it also returns Canadian provinces for cross-border carriers.
 */
const toUSStateAbbreviation = (value: unknown): string | undefined => {
  if (!hasText(value)) return undefined;
  const abbreviation = value.trim().toUpperCase();
  return US_STATES.some((state) => state.abbreviation === abbreviation)
    ? abbreviation
    : undefined;
};

/**
 * A number Highway actually reported. Zero counts: "we own no trailers" is a real answer and
 * suppressing it would silently hand the customer a blank where the registry had data.
 */
const hasCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

/**
 * The one rule this whole feature turns on, in one place.
 *
 * `existing` — whatever is already in sessionStorage for this field — wins outright and is
 * never overwritten. `account` — our own record — wins over Highway. Highway fills only what
 * neither of the first two had.
 *
 * Returns whether the winning value came from Highway, which is exactly the set of fields the
 * suggestion record has to remember — the ones a later carrier is allowed to replace.
 */
const resolveField = (
  existing: unknown,
  account: unknown,
  highway: unknown
): { value: string; fromHighway: boolean } | null => {
  if (hasText(existing)) return { value: existing.trim(), fromHighway: false };
  if (hasText(account)) return { value: account.trim(), fromHighway: false };
  if (hasText(highway)) return { value: highway.trim(), fromHighway: true };
  return null;
};

/**
 * A phone lands in the field in the shape the customer reads, not the shape the API stores.
 *
 * Both steps format as you type and re-parse to E.164 on submit, so a raw "+14052582066" in the
 * box is the one form the form itself never produces — it reads as a glitch on the very field
 * we are asking them to confirm. Values that aren't a dialable US number pass through unchanged.
 */
const toDisplayPhone = (value: unknown): unknown =>
  hasText(value) ? formatUSPhoneForDisplay(value) : value;

const readJson = <T>(raw: string | null): Partial<T> => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Partial<T>) : {};
  } catch {
    return {};
  }
};

/**
 * Drops the fields that still hold a previous carrier's untouched suggestion, so the carrier
 * the customer has NOW confirmed can fill them.
 *
 * Without this, a customer whose save failed, who went back and confirmed a different carrier,
 * would carry the first carrier's legal name, phone and address into their application — the
 * merge rules would see those values sitting in the session and dutifully protect them. Values
 * the customer has since edited are NOT dropped: those are their answers, not a stale
 * suggestion, and they outrank both carriers.
 */
const dropStaleSuggestions = (
  existing: Record<string, unknown>,
  previousSuggestions: SectionSuggestions
): Record<string, unknown> => {
  const kept: Record<string, unknown> = { ...existing };
  Object.entries(previousSuggestions).forEach(([field, suggested]) => {
    const current = String(kept[field] ?? '');
    // The phone suggestion is read back normalised to the shape the FIELD holds, while a session
    // written before that was true still holds raw E.164. Comparing both ways keeps the previous
    // carrier's number recognisable as its own untouched suggestion rather than as an answer the
    // customer gave — otherwise it would be protected and carried into the new carrier's form.
    const stillSuggested =
      current === suggested ||
      (current.length > 0 && formatUSPhoneForDisplay(current) === suggested);
    if (stillSuggested) delete kept[field];
  });
  return kept;
};

/**
 * The Business Info half: business name, business phone, the address, years in business, and
 * the state of registration inferred from that address. Nothing else — EIN, revenue, referral
 * code and factoring are all on the never-prefill list and are absent from `CarrierPrefill` to
 * begin with.
 *
 * Only keys with a resolved value are returned. That matters more than it looks: BusinessInfo
 * hydrates by iterating the stored object and calling `setValue` for every key it finds, so
 * writing a key with an empty string would actively CLEAR a field the form had filled from
 * somewhere else.
 */
export const buildBusinessInfoPrefill = (
  existing: Record<string, unknown>,
  account: PrefillAccountSnapshot,
  prefill: CarrierPrefill
): { fields: Record<string, string>; suggestions: SectionSuggestions } => {
  const fields: Record<string, string> = {};
  const suggestions: SectionSuggestions = {};

  const take = (key: string, accountValue: unknown, highwayValue: unknown) => {
    const resolved = resolveField(existing[key], accountValue, highwayValue);
    if (!resolved) return;
    fields[key] = resolved.value;
    if (resolved.fromHighway) suggestions[key] = resolved.value;
  };

  take(PREFILL_FIELD_BUSINESS_NAME, account.businessName, prefill.legal_name);
  // A phone Highway gives us that is not a dialable US number is worse than no phone at all —
  // the field is validated on submit, so an unparseable value would hand the customer an error
  // on a field they never touched.
  const highwayPhone = hasText(prefill.phone)
    ? parseUSPhoneToE164(prefill.phone)
    : null;
  take(
    PREFILL_FIELD_BUSINESS_PHONE,
    toDisplayPhone(account.businessPhone),
    highwayPhone ? formatUSPhoneForDisplay(highwayPhone) : undefined
  );

  /**
   * The address resolves as ONE unit, never part by part.
   *
   * Resolving each column on its own looks like the same rule applied six times, but it
   * silently splices sources: an account row holding only a city and state, plus a Highway
   * record for a carrier three states away, composes into a street that exists in neither —
   * and the customer, told only that we found it from their MC/DOT, submits it for KYB.
   * Whichever source is used, it is used whole.
   */
  const existingAddress: Record<string, unknown> = {};
  PREFILL_ADDRESS_PART_FIELDS.forEach((field) => {
    existingAddress[field] = existing[field];
  });
  const accountAddress: Record<string, unknown> = {
    address_st_number: account.address?.address_st_number,
    address_st_name: account.address?.address_st_name,
    address_unit: account.address?.address_unit,
    address_city: account.address?.address_city,
    address_state: account.address?.address_state,
    address_zip: account.address?.address_zip,
  };
  const highwayAddress: Record<string, unknown> = {
    address_st_number: prefill.address?.street_number,
    address_st_name: prefill.address?.street_name,
    address_unit: prefill.address?.unit,
    address_city: prefill.address?.city,
    address_state: prefill.address?.state,
    address_zip: prefill.address?.zip,
  };
  const anyPart = (parts: Record<string, unknown>) =>
    PREFILL_ADDRESS_PART_FIELDS.some((field) => hasText(parts[field]));

  const addressSource = anyPart(existingAddress)
    ? null // Already answered — leave every part exactly as it is.
    : anyPart(accountAddress)
      ? { parts: accountAddress, fromHighway: false }
      : anyPart(highwayAddress)
        ? { parts: highwayAddress, fromHighway: true }
        : null;

  if (addressSource) {
    PREFILL_ADDRESS_PART_FIELDS.forEach((field) => {
      const value = addressSource.parts[field];
      if (!hasText(value)) return;
      fields[field] = value.trim();
      if (addressSource.fromHighway) suggestions[field] = value.trim();
    });

    // FlexibleAddressInput opens in autocomplete mode and shows the single-line string, so the
    // parts alone would leave the customer looking at an empty box. Composed exactly as
    // populateApplicationData composes it, from the one source we just used.
    //
    // Only from a COMPLETE address, though. `getFullAddress` drops the empties, so a registry
    // address missing its street number composes into a line that looks right and is not, and
    // that line is what the step then treats as a Places selection. Partial stays partial: the
    // parts are written, the bar stays empty, and the customer is asked for the real address.
    const composed = isCompleteAddress(fields)
      ? getFullAddress({
          address_st_name: fields.address_st_name ?? '',
          address_st_number: fields.address_st_number ?? '',
          address_unit: fields.address_unit ?? '',
          address_city: fields.address_city ?? '',
          address_state: fields.address_state ?? '',
          address_zip: fields.address_zip ?? '',
        })
      : '';
    if (
      !hasText(existing[PREFILL_FIELD_BUSINESS_ADDRESS]) &&
      hasText(composed)
    ) {
      fields[PREFILL_FIELD_BUSINESS_ADDRESS] = composed;
      if (addressSource.fromHighway) {
        suggestions[PREFILL_FIELD_BUSINESS_ADDRESS] = composed;
      }
    }
  }

  /**
   * State of registration — the one field here that is INFERRED rather than found.
   *
   * No registry carries it, Highway included, so the state of the business address stands in for
   * it. Registering in Delaware while running out of Oklahoma is ordinary, so this WILL be wrong
   * for some carriers — it is a seeded default in an editable select, nothing more, and the
   * customer's own answer on the record still beats it.
   *
   * Read off the address that will be ON THE FORM — the one resolved just above, or the one
   * already in the session when the address was answered on an earlier pass. Keying it to the
   * address resolved *this* run instead would mean a second apply never derived anything at all:
   * a retry after a failed save finds the address already stored and leaves the block untouched,
   * which is exactly when the customer is most likely to be looking at the form.
   *
   * Because the guess is tied to whatever address the form ends up showing, it does not need the
   * one-source-only rule the address block itself lives by.
   *
   * It is recorded as a Highway SUGGESTION only when the address it was read off is one. The
   * suggestion record has a single job — marking what a later carrier is allowed to replace — and
   * a state inferred from the customer's own account address is not that; recording it there would
   * have one record mean two things and drop a value no carrier ever supplied.
   */
  const inferredState = toUSStateAbbreviation(
    fields.address_state ?? existing.address_state
  );
  const resolvedState = resolveField(
    existing[PREFILL_FIELD_STATE_OF_REGISTRATION],
    account.stateOfRegistration,
    inferredState
  );
  if (resolvedState) {
    fields[PREFILL_FIELD_STATE_OF_REGISTRATION] = resolvedState.value;
    if (resolvedState.fromHighway && addressSource?.fromHighway) {
      suggestions[PREFILL_FIELD_STATE_OF_REGISTRATION] = resolvedState.value;
    }
  }

  take(
    PREFILL_FIELD_YEARS_IN_BUSINESS,
    undefined,
    hasCount(prefill.years_in_business)
      ? String(prefill.years_in_business)
      : undefined
  );

  /**
   * Not a prefill — the factoring company is self-reported and Highway has none of it.
   *
   * It is carried over because BusinessInfo restores it from the customer record only when
   * NOTHING is in `businessInfo` storage, and this write puts something there. Without the
   * carry-over a returning customer with a factoring company on file would meet an empty
   * required field, and answering "No" to get past it would wipe the company already stored
   * against their record.
   */
  if (
    hasText(account.factoringCompanyName) &&
    !hasText(existing.factoring_company_name) &&
    !hasText(existing.uses_factoring)
  ) {
    fields.uses_factoring = 'yes';
    fields.factoring_company_name = account.factoringCompanyName.trim();
  }

  return { fields, suggestions };
};

/**
 * The Personal Info half. Name is a genuine Highway suggestion; email and phone are
 * ACCOUNT-FIRST — the customer logged in with an email and gave us a phone at signup, and
 * Highway's contact block is routinely a dispatcher's. Highway only ever fills the gap.
 */
export const buildPersonalInfoPrefill = (
  existing: Record<string, unknown>,
  account: PrefillAccountSnapshot,
  prefill: CarrierPrefill
): { fields: Record<string, string>; suggestions: SectionSuggestions } => {
  const fields: Record<string, string> = {};
  const suggestions: SectionSuggestions = {};

  const take = (key: string, accountValue: unknown, highwayValue: unknown) => {
    const resolved = resolveField(existing[key], accountValue, highwayValue);
    if (!resolved) return;
    fields[key] = resolved.value;
    if (resolved.fromHighway) suggestions[key] = resolved.value;
  };

  take(PREFILL_FIELD_FIRST_NAME, account.firstName, prefill.contact_first_name);
  take(PREFILL_FIELD_LAST_NAME, account.lastName, prefill.contact_last_name);
  take(PREFILL_FIELD_EMAIL, account.email, prefill.email);
  const highwayPhone = hasText(prefill.phone)
    ? parseUSPhoneToE164(prefill.phone)
    : null;
  take(
    PREFILL_FIELD_PHONE_NUMBER,
    toDisplayPhone(account.phoneNumber),
    highwayPhone ? formatUSPhoneForDisplay(highwayPhone) : undefined
  );

  return { fields, suggestions };
};

/**
 * The Assets half. Owned tractors/trailers only — fleet age, mileage and reserve status are not
 * observable from Highway and stay manual.
 */
export const buildAssetPrefill = (
  prefill: CarrierPrefill
): { assets: AssetPrefill; suggestions: SectionSuggestions } => {
  const assets: AssetPrefill = {};
  const suggestions: SectionSuggestions = {};

  if (hasCount(prefill.owned_tractors)) {
    assets.num_trucks = prefill.owned_tractors;
    suggestions[PREFILL_FIELD_NUM_TRUCKS] = String(prefill.owned_tractors);
  }
  if (hasCount(prefill.owned_trailers)) {
    assets.num_trailers = prefill.owned_trailers;
    suggestions[PREFILL_FIELD_NUM_TRAILERS] = String(prefill.owned_trailers);
  }

  return { assets, suggestions };
};

/**
 * sessionStorage is unavailable outright in some privacy modes and throws on write once a quota
 * is hit. Prefill is a convenience with no correctness stake at all — a browser that refuses
 * storage simply gets the un-prefilled flow, which is the same thing a Highway miss gives it.
 */
/**
 * A phone suggestion is compared against what the FIELD holds, and the field holds a formatted
 * number — both when the prefill writes one and when the step hydrates one (see
 * `normalizeStoredPhone`). A session written before that was true stores E.164 on both sides, so
 * normalising the suggestion the same way keeps a number the customer never touched recognisable
 * as ours, instead of reading the reformat as an edit and protecting it from the next carrier.
 */
const normalizePhoneSuggestion = (
  suggestions: SectionSuggestions,
  field: string
): SectionSuggestions =>
  hasText(suggestions[field])
    ? { ...suggestions, [field]: formatUSPhoneForDisplay(suggestions[field]) }
    : suggestions;

/** The suggestion record as stored, or an empty one when absent or unreadable. */
const readSuggestions = (): PrefillSuggestions => {
  const parsed = readJson<PrefillSuggestions>(
    readSession(PREFILL_SUGGESTIONS_STORAGE_KEY)
  );
  const section = (value: unknown): SectionSuggestions =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as SectionSuggestions)
      : {};
  return {
    carrierKey: typeof parsed.carrierKey === 'string' ? parsed.carrierKey : '',
    businessInfo: normalizePhoneSuggestion(
      section(parsed.businessInfo),
      PREFILL_FIELD_BUSINESS_PHONE
    ),
    personalInfo: normalizePhoneSuggestion(
      section(parsed.personalInfo),
      PREFILL_FIELD_PHONE_NUMBER
    ),
    assets: section(parsed.assets),
  };
};

/**
 * What a step should PUT IN the phone field for a value restored from session storage.
 *
 * The write side formats, but the session can already hold a raw E.164 number — a draft saved
 * before this shipped, or a submit from an older tab — and the merge rules protect whatever is
 * there rather than replacing it. Formatting on the way into the field means the customer sees
 * one shape whatever wrote it, without a migration or a cleared session.
 */
export const normalizeStoredPhone = (value: unknown): string =>
  hasText(value) ? formatUSPhoneForDisplay(value) : '';

/**
 * Merges a prefill payload into the session shapes the signup steps already hydrate from.
 *
 * `carrierKey` identifies the carrier the customer confirmed. Re-applying for the SAME carrier
 * is idempotent — a retry after a failed patch finds its own values in `existing` and changes
 * nothing, so it cannot stomp anything typed in between. Applying for a DIFFERENT carrier
 * first clears the previous carrier's untouched suggestions, so the customer who corrected
 * their number gets the carrier they actually confirmed rather than the one they rejected.
 */
export const applyCarrierPrefill = (
  prefill: CarrierPrefill,
  account: PrefillAccountSnapshot = {},
  carrierKey = ''
): PrefillSuggestions => {
  const previous = readSuggestions();
  const isSameCarrier = previous.carrierKey === carrierKey;

  const storedBusiness = readJson<Record<string, unknown>>(
    readSession(BUSINESS_INFO_STORAGE_KEY)
  );
  const storedPersonal = readJson<Record<string, unknown>>(
    readSession(PERSONAL_INFO_STORAGE_KEY)
  );
  const existingBusiness = isSameCarrier
    ? storedBusiness
    : dropStaleSuggestions(storedBusiness, previous.businessInfo);
  const existingPersonal = isSameCarrier
    ? storedPersonal
    : dropStaleSuggestions(storedPersonal, previous.personalInfo);

  const business = buildBusinessInfoPrefill(existingBusiness, account, prefill);
  const personal = buildPersonalInfoPrefill(existingPersonal, account, prefill);
  const assets = buildAssetPrefill(prefill);

  /**
   * A drop is a change in its own right.
   *
   * Writing only when the new payload produced fields would leave the REJECTED carrier's values
   * sitting in the session whenever the confirmed one has nothing to put in their place — the
   * keys were dropped from `existing` in memory, the merged object was thrown away, and the
   * customer would carry the wrong company's contact name into their application.
   */
  const dropped = (
    stored: Record<string, unknown>,
    remaining: Record<string, unknown>
  ) => Object.keys(stored).length !== Object.keys(remaining).length;

  if (
    Object.keys(business.fields).length ||
    dropped(storedBusiness, existingBusiness)
  ) {
    writeSession(
      BUSINESS_INFO_STORAGE_KEY,
      JSON.stringify({ ...existingBusiness, ...business.fields })
    );
  }
  if (
    Object.keys(personal.fields).length ||
    dropped(storedPersonal, existingPersonal)
  ) {
    writeSession(
      PERSONAL_INFO_STORAGE_KEY,
      JSON.stringify({ ...existingPersonal, ...personal.fields })
    );
  }
  if (Object.keys(assets.assets).length) {
    writeSession(ASSET_PREFILL_STORAGE_KEY, JSON.stringify(assets.assets));
  } else if (!isSameCarrier) {
    // The counts have no per-field merge to protect them: they are written whole or not at all.
    // A carrier the customer has now REJECTED must not keep seeding the Assets step just because
    // the one they confirmed has no equipment data of its own.
    removeSession(ASSET_PREFILL_STORAGE_KEY);
  }

  /**
   * A second apply for the SAME carrier keeps the first one's record.
   *
   * Both screens can apply the same payload — the heads-up page when it lands, Business Info when
   * it joins the same lookup — and the second pass finds every value already in `existing`, so it
   * resolves them all as "not from Highway" and would hand back an EMPTY record. That record is
   * the only thing that can later tell a value we filled in from one the customer typed, so
   * losing it silently disarms `dropStaleSuggestions`: the carrier they went back and rejected
   * would keep its legal name and address in the application. A different carrier still replaces
   * the record outright — that is the whole point of the drop above.
   */
  const keep = (
    previousSection: SectionSuggestions,
    fresh: SectionSuggestions
  ): SectionSuggestions =>
    isSameCarrier ? { ...previousSection, ...fresh } : fresh;

  const suggestions: PrefillSuggestions = {
    carrierKey,
    businessInfo: keep(previous.businessInfo, business.suggestions),
    personalInfo: keep(previous.personalInfo, personal.suggestions),
    assets: keep(previous.assets, assets.suggestions),
  };
  writeSession(PREFILL_SUGGESTIONS_STORAGE_KEY, JSON.stringify(suggestions));

  return suggestions;
};

/** The Assets step's read. Empty object when no prefill ever landed. */
export const readAssetPrefill = (): AssetPrefill => {
  const parsed = readJson<AssetPrefill>(readSession(ASSET_PREFILL_STORAGE_KEY));
  return {
    num_trucks: hasCount(parsed.num_trucks) ? parsed.num_trucks : undefined,
    num_trailers: hasCount(parsed.num_trailers)
      ? parsed.num_trailers
      : undefined,
  };
};

/**
 * Persists what the Assets step currently holds, so a customer who corrects a suggested count
 * and then steps back and forward again finds THEIR number rather than Highway's. Only ever
 * called by a step that was actually prefilled — with no payload the key stays absent and the
 * step behaves exactly as it did before this feature.
 */
export const saveAssetAnswers = (assets: AssetPrefill) => {
  writeSession(ASSET_PREFILL_STORAGE_KEY, JSON.stringify(assets));
};

/**
 * What a step's fields were suggested. Its one caller is the Assets step, which needs to know
 * whether a prefill ever reached it: the counts it stores are overwritten with the customer's
 * own the moment they touch a field, so the counts alone cannot answer that a second time.
 *
 * Empty for every legacy path — no payload, unreadable storage, malformed JSON — so a step with
 * nothing prefilled behaves exactly as it did before this feature.
 */
export const readPrefillSuggestions = (
  section: keyof Omit<PrefillSuggestions, 'carrierKey'>
): SectionSuggestions => readSuggestions()[section];
